import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  InfoBox,
  Input,
  Modal,
  Spinner,
  Table,
  Text,
  Tooltip,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdBlock, MdClose, MdRefresh, MdUndo } from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import { getThemeColor } from "theme/colors";
import {
  KbWebSourceDto,
  KbWebSourcePageDto,
  KbWebSourcePageStatus,
  WebSourcePagesSummary,
} from "types/knowledgeBase";
import {
  filterWebSourcePages,
  formatContentLength,
} from "./webSourcePageFilters";

interface WebSourcePagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  webSource: KbWebSourceDto | null;
}

type StatusFilter = "all" | KbWebSourcePageStatus;

const STATUS_COLORS: Record<KbWebSourcePageStatus, string> = {
  Indexed: "green",
  Skipped: "orange",
  Failed: "red",
  Syncing: "blue",
  Excluded: "gray",
};

const EMPTY_SUMMARY: WebSourcePagesSummary = {
  indexed: 0,
  skipped: 0,
  failed: 0,
  excluded: 0,
  truncated: 0,
};

/**
 * Listing of every URL from a web source's latest crawl, with per-row actions to re-crawl
 * a single page or exclude it from the knowledge base (singly or in bulk).
 *
 * The skipped/failed rows explain why a page an admin expects is missing; the exclude
 * action covers the opposite case — a crawl that pulled in pages that should not answer
 * questions.
 */
const WebSourcePagesModal = ({
  isOpen,
  onClose,
  webSource,
}: WebSourcePagesModalProps) => {
  const { t } = useTranslation();
  const [pages, setPages] = useState<KbWebSourcePageDto[]>([]);
  const [summary, setSummary] = useState<WebSourcePagesSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [syncingPageId, setSyncingPageId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [updatingExclusion, setUpdatingExclusion] = useState(false);

  const loadPages = useCallback(async () => {
    if (!webSource) return;

    setLoading(true);
    try {
      const result = await knowledgeBaseService.getWebSourcePages(webSource.id);
      setPages(result.pages);
      setSummary(result.summary ?? EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, [webSource]);

  useEffect(() => {
    if (isOpen && webSource) {
      setSearch("");
      setStatusFilter("all");
      setSelectedIds(new Set());
      void loadPages();
    }
  }, [isOpen, webSource, loadPages]);

  const handleSyncPage = async (page: KbWebSourcePageDto) => {
    if (!webSource) return;

    setSyncingPageId(page.id);
    try {
      await knowledgeBaseService.syncWebSourcePage(webSource.id, page.id);
      notify({
        type: "success",
        title: t("app.knowledgeBase.webSources.pages.syncQueued"),
      });
      await loadPages();
    } finally {
      setSyncingPageId(null);
    }
  };

  const handleSetExcluded = async (pageIds: string[], excluded: boolean) => {
    if (!webSource || pageIds.length === 0) return;

    setUpdatingExclusion(true);
    try {
      await knowledgeBaseService.setWebSourcePagesExcluded(
        webSource.id,
        pageIds,
        excluded,
      );
      notify({
        type: "success",
        title: t(
          excluded
            ? "app.knowledgeBase.webSources.pages.excludeSuccess"
            : "app.knowledgeBase.webSources.pages.includeSuccess",
          { count: pageIds.length },
        ),
      });
      setSelectedIds(new Set());
      await loadPages();
    } finally {
      setUpdatingExclusion(false);
    }
  };

  const toggleSelect = (pageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const filteredPages = useMemo(
    () => filterWebSourcePages(pages, search, statusFilter),
    [pages, search, statusFilter],
  );

  const statusFilters: StatusFilter[] = [
    "all",
    "Indexed",
    "Skipped",
    "Failed",
    "Excluded",
  ];

  // Select-all acts on what is visible, so "filter to Skipped, select all, exclude" works.
  const allVisibleSelected =
    filteredPages.length > 0 &&
    filteredPages.every((page) => selectedIds.has(page.id));
  const someVisibleSelected = filteredPages.some((page) =>
    selectedIds.has(page.id),
  );

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const page of filteredPages) {
        if (checked) {
          next.add(page.id);
        } else {
          next.delete(page.id);
        }
      }
      return next;
    });
  };

  const selectedPages = pages.filter((page) => selectedIds.has(page.id));
  const selectedToExclude = selectedPages
    .filter((page) => page.status !== "Excluded")
    .map((page) => page.id);
  const selectedToInclude = selectedPages
    .filter((page) => page.status === "Excluded")
    .map((page) => page.id);

  const describeReason = (page: KbWebSourcePageDto): string => {
    if (!page.reason) {
      return "";
    }

    const label = t(
      `app.knowledgeBase.webSources.pages.reasons.${page.reason}`,
    );

    // The status code is the actionable part of an HTTP failure, so surface it inline.
    return page.reason === "HttpError" && page.statusCode
      ? `${label} (${page.statusCode})`
      : label;
  };

  const formatDate = (value?: string): string =>
    value ? new Date(value).toLocaleString() : "—";

  if (!isOpen || !webSource) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <Box>
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.knowledgeBase.webSources.pages.title")}
            </Text>
            <Text fontSize="sm" color={getThemeColor("text.secondary")}>
              {webSource.name}
            </Text>
          </Box>
          <Modal.CloseTrigger asChild>
            <Button variant="ghost" size="sm" p={0}>
              <MdClose className="h-6 w-6" />
            </Button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        <Flex gap={3} mb={4} wrap="wrap" align="center">
          <Input
            flex={1}
            minW="220px"
            size="sm"
            value={search}
            placeholder={t(
              "app.knowledgeBase.webSources.pages.searchPlaceholder",
            )}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
          />
          <HStack gap={1}>
            {statusFilters.map((filter) => (
              <Button
                key={filter}
                size="sm"
                intent={statusFilter === filter ? "primary" : "secondary"}
                onClick={() => setStatusFilter(filter)}
              >
                {t(`app.knowledgeBase.webSources.pages.filters.${filter}`)}
              </Button>
            ))}
          </HStack>
        </Flex>

        <Text fontSize="sm" color={getThemeColor("text.secondary")} mb={3}>
          {t("app.knowledgeBase.webSources.pages.summary", {
            indexed: summary.indexed,
            skipped: summary.skipped,
            failed: summary.failed,
          })}
          {summary.excluded > 0 &&
            ` · ${t("app.knowledgeBase.webSources.pages.excludedCount", {
              count: summary.excluded,
            })}`}
          {summary.truncated > 0 &&
            ` · ${t("app.knowledgeBase.webSources.pages.truncated", {
              count: summary.truncated,
            })}`}
        </Text>

        {selectedIds.size > 0 && (
          <HStack
            gap={2}
            mb={3}
            px={3}
            py={2}
            borderRadius="md"
            bg={{ base: "blue.50", _dark: "blue.900" }}
            borderWidth="1px"
            borderColor={{ base: "blue.200", _dark: "blue.700" }}
          >
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={{ base: "blue.700", _dark: "blue.200" }}
            >
              {t("app.knowledgeBase.bulk.selected", {
                count: selectedIds.size,
              })}
            </Text>
            {selectedToExclude.length > 0 && (
              <Button
                size="xs"
                intent="danger"
                loading={updatingExclusion}
                onClick={() => handleSetExcluded(selectedToExclude, true)}
              >
                {t("app.knowledgeBase.webSources.pages.excludeSelected", {
                  count: selectedToExclude.length,
                })}
              </Button>
            )}
            {selectedToInclude.length > 0 && (
              <Button
                size="xs"
                intent="secondary"
                loading={updatingExclusion}
                onClick={() => handleSetExcluded(selectedToInclude, false)}
              >
                {t("app.knowledgeBase.webSources.pages.includeSelected", {
                  count: selectedToInclude.length,
                })}
              </Button>
            )}
            <Button
              size="xs"
              intent="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              {t("app.knowledgeBase.bulk.clearSelection")}
            </Button>
          </HStack>
        )}

        {loading ? (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        ) : pages.length === 0 ? (
          <InfoBox>
            {t("app.knowledgeBase.webSources.pages.emptyNeverCrawled")}
          </InfoBox>
        ) : filteredPages.length === 0 ? (
          <InfoBox>
            {t("app.knowledgeBase.webSources.pages.emptyNoMatches")}
          </InfoBox>
        ) : (
          <Box overflowX="auto" maxH="55vh" overflowY="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px">
                    <Checkbox
                      size="sm"
                      aria-label={t(
                        "app.knowledgeBase.webSources.pages.selectAll",
                      )}
                      checked={
                        allVisibleSelected
                          ? true
                          : someVisibleSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={(e: {
                        checked: boolean | "indeterminate";
                      }) => toggleSelectAllVisible(e.checked === true)}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.knowledgeBase.webSources.pages.table.page")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.knowledgeBase.webSources.pages.table.status")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.knowledgeBase.webSources.pages.table.size")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.knowledgeBase.webSources.pages.table.crawledAt")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    {t("app.common.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredPages.map((page) => (
                  <Table.Row
                    key={page.id}
                    opacity={page.status === "Excluded" ? 0.6 : 1}
                  >
                    <Table.Cell>
                      <Checkbox
                        size="sm"
                        aria-label={page.displayUrl || page.url}
                        checked={selectedIds.has(page.id)}
                        onCheckedChange={() => toggleSelect(page.id)}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Box>
                        {page.title && (
                          <Text fontWeight="medium" truncate maxW="380px">
                            {page.title}
                          </Text>
                        )}
                        <Tooltip content={page.displayUrl || page.url}>
                          <Text
                            fontSize="xs"
                            color={getThemeColor("text.secondary")}
                            truncate
                            maxW="380px"
                          >
                            {page.displayUrl || page.url}
                          </Text>
                        </Tooltip>
                      </Box>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={2}>
                        <Badge
                          colorPalette={STATUS_COLORS[page.status]}
                          size="sm"
                          variant="subtle"
                        >
                          {t(
                            `app.knowledgeBase.webSources.pages.statuses.${page.status}`,
                          )}
                        </Badge>
                        {page.reason && (
                          <Text
                            fontSize="xs"
                            color={getThemeColor("text.secondary")}
                          >
                            {describeReason(page)}
                          </Text>
                        )}
                        {page.status === "Excluded" && (
                          <Text
                            fontSize="xs"
                            color={getThemeColor("text.secondary")}
                          >
                            {t(
                              "app.knowledgeBase.webSources.pages.excludedHint",
                            )}
                          </Text>
                        )}
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="sm"
                        color={getThemeColor("text.secondary")}
                      >
                        {formatContentLength(page.contentLength)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="sm"
                        color={getThemeColor("text.secondary")}
                      >
                        {formatDate(page.crawledAt)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Flex justify="flex-end" gap={1}>
                        <Tooltip
                          content={t(
                            page.status === "Excluded"
                              ? "app.knowledgeBase.webSources.pages.syncExcluded"
                              : "app.knowledgeBase.webSources.pages.syncPage",
                          )}
                        >
                          <IconButton
                            aria-label={t(
                              "app.knowledgeBase.webSources.pages.syncPage",
                            )}
                            size="sm"
                            variant="ghost"
                            loading={syncingPageId === page.id}
                            disabled={
                              page.status === "Syncing" ||
                              page.status === "Excluded" ||
                              syncingPageId !== null
                            }
                            onClick={() => handleSyncPage(page)}
                          >
                            <MdRefresh />
                          </IconButton>
                        </Tooltip>
                        {page.status === "Excluded" ? (
                          <Tooltip
                            content={t(
                              "app.knowledgeBase.webSources.pages.include",
                            )}
                          >
                            <IconButton
                              aria-label={t(
                                "app.knowledgeBase.webSources.pages.include",
                              )}
                              size="sm"
                              variant="ghost"
                              disabled={updatingExclusion}
                              onClick={() =>
                                handleSetExcluded([page.id], false)
                              }
                            >
                              <MdUndo />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            content={t(
                              "app.knowledgeBase.webSources.pages.exclude",
                            )}
                          >
                            <IconButton
                              aria-label={t(
                                "app.knowledgeBase.webSources.pages.exclude",
                              )}
                              size="sm"
                              variant="ghost"
                              disabled={updatingExclusion}
                              onClick={() => handleSetExcluded([page.id], true)}
                            >
                              <MdBlock />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button intent="secondary" onClick={onClose}>
          {t("app.common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WebSourcePagesModal;
